import { useErpSourceList } from 'context/Store';
import { useTranslation } from 'react-i18next';

import FoldableCard from 'components/desktop/_general/Card/FoldableCard/FoldableCard';
import ProductCard from 'components/desktop/_general/Card/ProductCard/ProductCard';
import CardSlider, { SliderCard } from 'components/desktop/_general/CardSlider/CardSlider';
import { Match } from 'components/desktop/_general/Conditional/Match';
import LoadingSpinner from 'components/desktop/_general/Loading/LoadingSpinner';
import { useReaction } from 'hooks/useReaction';
import { ProductList } from 'types/Recipe/Detail/ErpNew/ProductList';
import { getAllIngredientIds } from 'types/Recipe/Segment';
import { Segment } from 'types/Recipe/Segment';
import { ErpSourceList } from 'types/_general/Store/ErpSourceList';

interface IProps {
	segments: Segment[];
}

type Product = {
	name: string | null;
	externalId: string | null;
	externalUrl: string | null;
	brand: string | null;
	price: number | null;
	imageUrl: string | null;
};

export const IngredientsProductSlider = ({ segments }: IProps) => {
	const { t } = useTranslation();
	const sources = useErpSourceList();

	const getGtinString = (input: string | null): string | null => {
		if (input === null) return null;
		if (input.length < 16) return input;
		return input.slice(0, 16) + '…';
	};

	const [productMatches, isLoaded] = useReaction<Product[], Segment[]>(
		[],
		async (input: Segment[]) => {
			const response = await ProductList.getFromApiSggestions(getAllIngredientIds(input));
			const products = ProductList.mapFromApiSuggestions(response);
			return products
				.map((e) => ({
					...e,
					name: e.name,
					externalId: getGtinString(e.gtin),
					externalUrl: e.externalUrl ?? null,
					brand: ErpSourceList.getNameById(sources, e.sourceId),
					price: e.price,
					imageUrl: e.imageUrl,
				}))
				.filter((e) => e.brand !== 'Default');
		},
		segments
	);

	const getContent = (): JSX.Element => {
		if (!isLoaded) return <LoadingSpinner />;
		return (
			<Match conditions={[productMatches.length === 0]}>
				<div style={{ textAlign: 'center' }}>
					<h2>{t('recipe:NO_SIMILAR_PRODUCTS_FOUND')}</h2>
				</div>
				<CardSlider
					cards={productMatches.map(
						(x) =>
							({
								content: <ProductCard product={x} />,
							}) as SliderCard
					)}
				></CardSlider>
			</Match>
		);
	};

	return (
		<FoldableCard
			defaultOpen={true}
			dataComponentType="recipe-products-card"
			title={t('recipe:PRODUCTS')}
			ai={true}
			content={getContent()}
		/>
	);
};
